import type { HTMLAttributes, MouseEvent } from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import {
  AngleLeft16,
  AngleRight16,
  ConfiguratorChevronLeft,
  ConfiguratorChevronRight
} from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { Calendar } from 'react-calendar';
import { COLOR_BLACK, CUSTOM_CLASS_NAME } from './constants';
import { DatepickerLayout, NavigationLabel } from './DatepickerLayout';
import { disableTile } from './disableTile';
import { getEffectiveLocale } from './get-effective-locale';

export type DatepickerProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> & {
  startDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  size?: number;
  /** Locale that should be used by the calendar. Can be any [IETF language tag](https://en.wikipedia.org/wiki/IETF_language_tag). */
  locale?: string;
  fontSize?: number;
  fontColor?: string;
  fontFamily?: string;
  accentColor?: string;
  backgroundColor?: string;
  cornerRadius?: number;
  borderRadius?: number;
  padding?: number;
  disablePreviousDaysAndMonths?: boolean;
  onChange: (date: Date, event?: MouseEvent) => void;
  withShadow?: boolean;
};

const Datepicker = forwardRef<HTMLDivElement, DatepickerProps>(
  (
    {
      startDate,
      minDate,
      maxDate,
      size = 42,
      locale = window.navigator.language,
      padding = 24,
      fontSize = 15,
      fontColor = COLOR_BLACK,
      accentColor = '#197bff',
      backgroundColor = 'transparent',
      disablePreviousDaysAndMonths = false,
      cornerRadius = 4,
      borderRadius = 8,
      fontFamily,
      withShadow = true,
      onChange,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const actualMinDate =
      minDate || (disablePreviousDaysAndMonths ? new Date() : null);

    const formatShortWeekday = (locale: string | undefined, date: Date) => {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'narrow'
      }).format(date);
    };

    const formatMonthYear = (locale: string | undefined, date: Date) => {
      return date.toLocaleDateString(locale, {
        month: 'long',
        year: 'numeric'
      });
    };

    const formatDay = (locale: string | undefined, date: Date) => {
      return date.toLocaleDateString(locale, { day: 'numeric' });
    };

    useEffect(() => {
      calendarRef.current?.setAttribute('tabindex', '-1');
    }, []);

    return (
      <DatepickerLayout
        ref={forwardingRef}
        _size={size}
        _color={fontColor}
        _padding={padding}
        _fontSize={fontSize}
        _accentColor={accentColor}
        _cornerRadius={cornerRadius}
        _borderRadius={borderRadius}
        _backgroundColor={backgroundColor}
        _fontFamily={fontFamily}
        _withShadow={withShadow}
        {...forwardingProps}
      >
        <Calendar
          value={startDate}
          locale={getEffectiveLocale(locale)}
          className={CUSTOM_CLASS_NAME}
          minDate={new Date('1901-01-01')}
          maxDate={maxDate}
          tileDisabled={(args) => disableTile(actualMinDate, args)}
          navigationLabel={NavigationLabel}
          next2Label={
            <Icon
              component={ConfiguratorChevronRight}
              size={14}
              fill={fontColor}
            />
          }
          prev2Label={
            <Icon
              component={ConfiguratorChevronLeft}
              size={14}
              fill={fontColor}
            />
          }
          nextLabel={
            <Icon component={AngleRight16} size={14} fill={fontColor} />
          }
          prevLabel={
            <Icon component={AngleLeft16} size={14} fill={fontColor} />
          }
          onChange={(date, event) => {
            if (date) {
              onChange(date as Date, event);
            }
          }}
          formatShortWeekday={formatShortWeekday}
          formatMonthYear={formatMonthYear}
          formatDay={formatDay}
          inputRef={calendarRef}
        />
      </DatepickerLayout>
    );
  }
);

export default Datepicker;
